import React, { useState, useEffect } from "react";
import { Outlet, NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import {
  LayoutDashboard,
  BookOpen,
  Award,
  Search,
  LogOut,
  ChevronRight,
  Menu,
  X,
  Settings,
  TrendingUp,
  Moon,
  Sun,
  Library,
} from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import GlobalSearch from "@/components/GlobalSearch";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_exec-talent-hub-1/artifacts/7a127wcx_Color%20logo%20-%20no%20background.svg";

const NAV_ITEMS = [
  { path: "/", icon: LayoutDashboard, label: "Dashboard", exact: true },
  { path: "/progression", icon: TrendingUp, label: "Progression" },
  { path: "/certification", icon: Award, label: "Certification" },
  { path: "/resources", icon: Library, label: "Research & Resources" },
];

const MODULE_NAV = Array.from({ length: 22 }, (_, i) => ({
  path: `/modules/${i + 1}`,
  number: i + 1,
}));

const MODULE_SHORT_TITLES = [
  "Foundations",
  "Client Acquisition",
  "Role Intake",
  "Research & Mapping",
  "Candidate ID",
  "Evaluation",
  "Presentation",
  "Interview Mgmt",
  "Offer Negotiation",
  "Post-Placement",
  "Mindset & Ethics",
  "BD Fundamentals",
  "Getting Meetings",
  "Flip Calling",
  "Spec Strategy",
  "Pitching & Meetings",
  "Account Mgmt",
  "Desk Building",
  "Personal Brand",
  "Operating System",
  "AI Tools",
  "Contracts & Fees",
];

export default function Layout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const isAdmin = user?.role === "admin";

  // Handle dark mode
  useEffect(() => {
    const savedMode = localStorage.getItem('darkMode');
    if (savedMode === 'true') {
      setDarkMode(true);
      document.documentElement.classList.add('dark');
    }
  }, []);

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    if (!darkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('darkMode', 'true');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('darkMode', 'false');
    }
  };

  // Keyboard shortcut for search
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const isActive = (path, exact) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const sidebarContent = (
    <div className="flex flex-col h-full">
      {/* Brand with Logo */}
      <div className="px-5 py-4 border-b border-slate-200">
        <img
          src={LOGO_URL}
          alt="Axentia Global"
          className="h-7 mb-1"
          data-testid="sidebar-logo"
        />
        <div className="text-[10px] text-slate-500 font-body tracking-widest uppercase">
          Executive Search Academy
        </div>
      </div>

      <ScrollArea className="flex-1">
        {/* Main Nav */}
        <div className="px-3 py-4">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path, item.exact);
            return (
              <NavLink
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-2.5 text-sm font-medium rounded-sm mx-1 mb-0.5 transition-all duration-200 ${
                  active
                    ? "text-slate-900 bg-white shadow-sm border border-slate-100"
                    : "text-slate-600 hover:bg-white hover:text-slate-900 hover:shadow-sm"
                }`}
              >
                <Icon className="w-4 h-4" strokeWidth={1.5} />
                {item.label}
              </NavLink>
            );
          })}
          {isAdmin && (
            <NavLink
              to="/admin"
              data-testid="nav-admin"
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-4 py-2.5 text-sm font-medium rounded-sm mx-1 mb-0.5 transition-all duration-200 ${
                isActive("/admin")
                  ? "text-slate-900 bg-white shadow-sm border border-slate-100"
                  : "text-slate-600 hover:bg-white hover:text-slate-900 hover:shadow-sm"
              }`}
            >
              <Settings className="w-4 h-4" strokeWidth={1.5} />
              Admin Panel
            </NavLink>
          )}
        </div>

        <Separator className="mx-4" />

        {/* Module Nav */}
        <div className="px-3 py-4">
          <div className="px-4 py-2 text-xs text-slate-400 uppercase tracking-wider font-semibold">
            Core Modules
          </div>
          {MODULE_NAV.filter(m => m.number <= 11).map((mod, idx) => {
            const active = isActive(mod.path);
            return (
              <NavLink
                key={mod.path}
                to={mod.path}
                data-testid={`nav-module-${mod.number}`}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-2 text-sm rounded-sm mx-1 mb-0.5 transition-all duration-200 ${
                  active
                    ? "text-slate-900 bg-white shadow-sm border border-slate-100 font-medium"
                    : "text-slate-600 hover:bg-white hover:text-slate-900 hover:shadow-sm"
                }`}
              >
                <span
                  className={`flex items-center justify-center w-5 h-5 rounded-sm text-xs font-semibold ${
                    active
                      ? "bg-slate-900 text-white"
                      : "bg-slate-200 text-slate-600"
                  }`}
                >
                  {mod.number}
                </span>
                <span className="truncate">{MODULE_SHORT_TITLES[idx]}</span>
                {active && <ChevronRight className="w-3 h-3 ml-auto text-slate-400" />}
              </NavLink>
            );
          })}
          <div className="px-4 py-2 mt-3 text-xs text-[#005d6c] uppercase tracking-wider font-semibold">
            BD & Revenue
          </div>
          {MODULE_NAV.filter(m => m.number > 11 && m.number <= 20).map((mod) => {
            const idx = mod.number - 1;
            const active = isActive(mod.path);
            return (
              <NavLink
                key={mod.path}
                to={mod.path}
                data-testid={`nav-module-${mod.number}`}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-2 text-sm rounded-sm mx-1 mb-0.5 transition-all duration-200 ${
                  active
                    ? "text-slate-900 bg-white shadow-sm border border-slate-100 font-medium"
                    : "text-slate-600 hover:bg-white hover:text-slate-900 hover:shadow-sm"
                }`}
              >
                <span
                  className={`flex items-center justify-center w-5 h-5 rounded-sm text-xs font-semibold ${
                    active
                      ? "bg-[#005d6c] text-white"
                      : "bg-[#005d6c]/10 text-[#005d6c]"
                  }`}
                >
                  {mod.number}
                </span>
                <span className="truncate">{MODULE_SHORT_TITLES[idx]}</span>
                {active && <ChevronRight className="w-3 h-3 ml-auto text-slate-400" />}
              </NavLink>
            );
          })}
          <div className="px-4 py-2 mt-3 text-xs text-amber-600 uppercase tracking-wider font-semibold">
            Advanced
          </div>
          {MODULE_NAV.filter(m => m.number > 20).map((mod) => {
            const idx = mod.number - 1;
            const active = isActive(mod.path);
            return (
              <NavLink
                key={mod.path}
                to={mod.path}
                data-testid={`nav-module-${mod.number}`}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-2 text-sm rounded-sm mx-1 mb-0.5 transition-all duration-200 ${
                  active
                    ? "text-slate-900 bg-white shadow-sm border border-slate-100 font-medium"
                    : "text-slate-600 hover:bg-white hover:text-slate-900 hover:shadow-sm"
                }`}
              >
                <span
                  className={`flex items-center justify-center w-5 h-5 rounded-sm text-xs font-semibold ${
                    active
                      ? "bg-amber-500 text-white"
                      : "bg-amber-100 text-amber-700"
                  }`}
                >
                  {mod.number}
                </span>
                <span className="truncate">{MODULE_SHORT_TITLES[idx]}</span>
                {active && <ChevronRight className="w-3 h-3 ml-auto text-slate-400" />}
              </NavLink>
            );
          })}
        </div>
      </ScrollArea>

      {/* User info + Logout */}
      <div className="px-3 py-4 border-t border-slate-200">
        {user && (
          <div className="px-4 py-1.5 mb-1 text-xs text-slate-500">
            <span className="font-medium text-slate-700">{user.full_name || user.username}</span>
            {isAdmin && <span className="ml-1.5 px-1.5 py-0.5 bg-[#005d6c] text-white rounded-sm text-[10px] uppercase tracking-wider">Admin</span>}
          </div>
        )}
        <button
          data-testid="logout-button"
          onClick={logout}
          className="flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-white rounded-sm mx-1 w-full transition-all duration-200"
        >
          <LogOut className="w-4 h-4" strokeWidth={1.5} />
          Sign Out
        </button>
      </div>
    </div>
  );

  return (
    <div className={`min-h-screen ${darkMode ? 'dark bg-slate-900' : 'bg-[#f8fafc]'}`}>
      {/* Global Search Modal */}
      <GlobalSearch isOpen={searchOpen} onClose={() => setSearchOpen(false)} />

      {/* Mobile header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-4 py-3 flex items-center justify-between">
        <img src={LOGO_URL} alt="Axentia Global" className="h-5" />
        <div className="flex items-center gap-2">
          <button
            data-testid="mobile-search-toggle"
            onClick={() => setSearchOpen(true)}
            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-sm"
          >
            <Search className="w-5 h-5 text-slate-600 dark:text-slate-300" />
          </button>
          <button
            data-testid="dark-mode-toggle-mobile"
            onClick={toggleDarkMode}
            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-sm"
          >
            {darkMode ? <Sun className="w-5 h-5 text-amber-500" /> : <Moon className="w-5 h-5 text-slate-600" />}
          </button>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-sm"
          >
            {sidebarOpen ? <X className="w-5 h-5 dark:text-white" /> : <Menu className="w-5 h-5 dark:text-white" />}
          </button>
        </div>
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-black/20"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-40 h-screen w-64 bg-slate-50 dark:bg-slate-800 border-r border-slate-200 dark:border-slate-700 transition-transform duration-200 lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {sidebarContent}
      </aside>

      {/* Main */}
      <main className="lg:ml-64 pt-14 lg:pt-0 min-h-screen flex flex-col">
        {/* Desktop Top Bar with Search and Dark Mode */}
        <div className="hidden lg:flex items-center justify-end gap-3 px-6 py-3 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
          <button
            onClick={() => setSearchOpen(true)}
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-700 rounded-sm hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
            data-testid="desktop-search-btn"
          >
            <Search className="w-4 h-4" />
            <span>Search</span>
            <kbd className="hidden sm:inline-flex ml-2 px-1.5 py-0.5 text-[10px] font-mono bg-white dark:bg-slate-600 rounded">⌘K</kbd>
          </button>
          <button
            onClick={toggleDarkMode}
            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-sm transition-colors"
            data-testid="dark-mode-toggle"
          >
            {darkMode ? <Sun className="w-5 h-5 text-amber-500" /> : <Moon className="w-5 h-5 text-slate-600" />}
          </button>
        </div>

        <div className="flex-1 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full">
          <Outlet />
        </div>

        {/* Footer */}
        <footer className="border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 mt-auto">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-center sm:text-left">
                <div className="text-sm font-semibold text-slate-900 dark:text-white">Axentia Global Executive Search Academy</div>
                <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">© {new Date().getFullYear()} Axentia Global. All rights reserved.</div>
              </div>
              <div className="flex items-center gap-4 text-xs text-slate-500 dark:text-slate-400">
                <a href="mailto:support@axentiaglobal.com" className="hover:text-slate-900 dark:hover:text-white transition-colors">Support</a>
                <span>•</span>
                <a href="mailto:contact@axentiaglobal.com" className="hover:text-slate-900 dark:hover:text-white transition-colors">Contact</a>
              </div>
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
}
